import React, { useEffect } from 'react';
import { ADMIN_PATH, PortalProvider, STAFF_PATH, STAFF_RESET_PATH, STAFF_SETUP_PATH, usePortal } from './contexts/PortalContext';
import { Login } from './pages/Login';
import { StaffLogin } from './pages/StaffLogin';
import { StaffSetupPassword } from './pages/StaffSetupPassword';
import { ClientApp } from './pages/ClientApp';
import { PortalApp } from './pages/PortalApp';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ViewToggle } from './components/ViewToggle';
import { Toast } from './components/Toast';
import { CLIENT_ACCESS_TITLE, STAFF_TITLE, projectTitle, setPageTitle } from './utils/pageTitle';
function Portal() {
  const {
    path,
    navigate,
    staffAuthed,
    staffChecking,
    authed,
    activeProject,
    clientPreview,
    previewRequested
  } = usePortal();
  const onAdmin = path.startsWith(ADMIN_PATH);
  /* Checked first: an invitation link must never fall through to sign-in. */
  const onStaffSetup = path.startsWith(STAFF_SETUP_PATH) || path.startsWith(STAFF_RESET_PATH);
  const onStaffLogin = !onStaffSetup && path.startsWith(STAFF_PATH);

  /* Staff pages carry the Portal name; a client tab carries their own, taken
     from the stored client_name once the project has loaded. */
  useEffect(() => {
    const staffSide = onAdmin || onStaffLogin || onStaffSetup;
    if (staffSide) {
      setPageTitle(STAFF_TITLE);
      return;
    }
    if (!authed) {
      setPageTitle(CLIENT_ACCESS_TITLE);
      return;
    }
    setPageTitle(projectTitle(activeProject.client));
  }, [onAdmin, onStaffLogin, onStaffSetup, authed, activeProject.client]);

  // /admin is protected: without a verified Supabase staff session you are sent
  // to /staff, and knowing the URL is never enough. Signed in, /staff hands you
  // on to /admin. Both wait for the persisted session to be checked first.
  useEffect(() => {
    if (staffChecking) return;
    if (onAdmin && !staffAuthed) navigate(STAFF_PATH);
    if (onStaffLogin && staffAuthed) navigate(ADMIN_PATH);
  }, [onAdmin, onStaffLogin, staffAuthed, staffChecking, navigate]);

  /* An invitation link: the session it carries is read here, so no redirect
     and no session check runs first. */
  if (onStaffSetup) {
    return <>
        <StaffSetupPassword data-id="element-0" data-mp="d2bhxfqdpvv5" />
        <Toast data-id="element-1" data-mp="851h73xjhxs0" />
      </>;
  }
  if (onAdmin) {
    if (staffChecking) return <div className="min-h-screen w-full bg-ink" data-id="element-2" data-mp="3glea6jyr55fp" />;
    if (!staffAuthed) {
      return <>
          <StaffLogin data-id="element-3" data-mp="3bmjq7ep1tau0" />
          <Toast data-id="element-4" data-mp="3bn314bga6yyj" />
        </>;
    }
    return <>
        <PortalApp data-id="element-5" data-mp="3qb7k7gj8xchu" />
        <ViewToggle data-id="element-6" data-mp="3qbpq74vpn52y" />
        <Toast data-id="element-7" data-mp="3qbzfsrcufa84" />
      </>;
  }
  if (onStaffLogin) {
    /* Nothing renders until the persisted session has been checked, so an
       already-signed-in staff member never sees the login form flash. */
    if (staffChecking) return <div className="min-h-screen w-full bg-ink" data-id="element-8" data-mp="2xssh27f8bsyt" />;
    return <>
        <StaffLogin data-id="element-9" data-mp="2sv0m14jafqqe" />
        <ViewToggle data-id="element-10" data-mp="2stpmxi0sf75m" />
        <Toast data-id="element-11" data-mp="18drky0d87nfx" />
      </>;
  }

  /* A preview link holds until the staff session has been verified, so the
     client name prompt never flashes on the way in. */
  if (previewRequested && staffChecking) {
    return <div className="min-h-screen w-full bg-paper" data-id="element-12" data-mp="yg3mp0gevxt0" />;
  }

  /* Preview as Client is the client link itself, read by a staff session, so
     it skips the name prompt rather than taking a different route. */
  if (!authed && !clientPreview) {
    return <>
        <Login data-id="element-13" data-mp="1r2g0e6sxx0kb" />
        <ViewToggle data-id="element-14" data-mp="1w3c68pwuqwsd" />
      </>;
  }
  return <>
      <ClientApp data-id="element-15" data-mp="1w2u113s7p88b" />
      {/* Nothing is added around a preview: it must look exactly like what
          the client opens. */}
      {!clientPreview && <ViewToggle data-id="element-16" data-mp="1m4wcsgacrbfp" />}
      {!clientPreview && <Toast data-id="element-17" data-mp="1m3uwnxehbyr4" />}
    </>;
}
export function App() {
  return <PortalProvider data-id="element-18" data-mp="6nw783cn6rg7">
      <ErrorBoundary data-id="element-19" data-mp="6ncm6drp3mxy">
        <Portal data-id="element-20" data-mp="6muln4y02mk8" />
      </ErrorBoundary>
    </PortalProvider>;
}